import { initializeApp, getApps } from 'firebase/app'
import { collection, doc, getDoc, getDocs, getFirestore } from 'firebase/firestore'
import { defaultExperience, defaultProfile, defaultProjects, defaultSkillCategories } from './data/defaultPortfolioData'

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || ''
}

const isFirebaseConfigured = Object.values(firebaseConfig).some((value) => Boolean(value))

const firebaseApp = isFirebaseConfigured
  ? getApps().length > 0
    ? getApps()[0]
    : initializeApp(firebaseConfig)
  : null

const db = firebaseApp ? getFirestore(firebaseApp) : null

export const getProfileConfig = async () => {
  if (!db) return defaultProfile

  try {
    const snapshot = await getDoc(doc(db, 'siteConfig', 'profile'))

    if (!snapshot.exists()) {
      return defaultProfile
    }

    return {
      ...defaultProfile,
      ...snapshot.data(),
      contact: {
        ...defaultProfile.contact,
        ...(snapshot.data().contact || {})
      },
      links: {
        ...defaultProfile.links,
        ...(snapshot.data().links || {})
      },
      id: snapshot.id
    }
  } catch (error) {
    console.error('Failed to load Firestore profile configuration', error)
    return defaultProfile
  }
}

const normalizeArray = (value, fallback = []) => (Array.isArray(value) ? value : fallback)

const normalizeExperienceItem = (item) => ({
  ...item,
  achievements: normalizeArray(item?.achievements)
})

const normalizeProjectItem = (item) => ({
  ...item,
  technologies: normalizeArray(item?.technologies),
  highlights: normalizeArray(item?.highlights),
  fullAchievements: normalizeArray(item?.fullAchievements),
  links: item?.links && typeof item.links === 'object' ? item.links : {}
})

const normalizeSkillItem = (item) => ({
  ...item,
  skills: normalizeArray(item?.skills),
  useCases: normalizeArray(item?.useCases),
  color: item?.color || '#10b981'
})

const getFirestoreCollection = async (collectionName, fallback) => {
  if (!db) return fallback

  try {
    const snapshot = await getDocs(collection(db, collectionName))

    if (snapshot.empty) {
      return fallback
    }

    const items = snapshot.docs.map((doc) => ({
      ...doc.data(),
      id: doc.id
    }))

    if (collectionName === 'experience') {
      return items.map(normalizeExperienceItem)
    }

    if (collectionName === 'projects') {
      return items.map(normalizeProjectItem)
    }

    if (collectionName === 'skills') {
      return items.map(normalizeSkillItem)
    }

    return items
  } catch (error) {
    console.error(`Failed to load Firestore collection: ${collectionName}`, error)
    return fallback
  }
}

export const getPortfolioConfig = async () => {
  if (!db) {
    return {
      experiences: defaultExperience,
      projects: defaultProjects,
      skills: defaultSkillCategories,
      source: 'default'
    }
  }

  try {
    const [experiences, projects, skills] = await Promise.all([
      getFirestoreCollection('experience', defaultExperience),
      getFirestoreCollection('projects', defaultProjects),
      getFirestoreCollection('skills', defaultSkillCategories)
    ])

    return {
      experiences,
      projects,
      skills,
      source: 'firestore'
    }
  } catch (error) {
    console.error('Unable to fetch Firestore portfolio data. Falling back to defaults.', error)
    return {
      experiences: defaultExperience,
      projects: defaultProjects,
      skills: defaultSkillCategories,
      source: 'default'
    }
  }
}

export { db }
